/**
 * Ted #32a (E1.6, E1.7, #1156): where a vaccination row's values came from,
 * and what the desk reads beside it.
 *
 * THE COMPLAINT THIS ANSWERS. Ted, 2026-10-06, on a migrated dog's profile:
 * every entry said "unverified". They were not owner uploads waiting for
 * review. They were Gingr's own records, carried over by the sync, which
 * never sets `verified` (only the intake approve route did). "Unverified"
 * told the desk a task was waiting when none was.
 *
 * THE RULE. `verified` stays the one sign-off fact: a staff member checked
 * this record against a certificate. `source` records who supplied the
 * row's CURRENT values, and decides what an unsigned row is called:
 *
 *   verified                 → "Verified" (whoever supplied it)
 *   not verified, 'gingr'    → "From Gingr"         (carried over, no task)
 *   not verified, 'staff'    → "Entered by staff"   (desk typed it, not checked)
 *   not verified, otherwise  → "Unverified"         (owner-entered, or unknown)
 *
 * so "Unverified" is reserved for what Ted asked: values a customer typed,
 * which a human still owes a look. An unknown source (NULL: written before
 * the column existed and not identifiable by the backfill) stays
 * "Unverified", which is the cautious reading and is what it said before.
 *
 * A staff EDIT makes the row 'staff', because the values are now the desk's,
 * not Gingr's or the owner's. Attaching a certificate does not change the
 * source or the sign-off. Neither does verifying.
 *
 * Pure: no I/O, no Prisma. The server serializers and the admin app both
 * import it, so the label and the rule cannot drift apart.
 */

/** The value set of `vaccinations.source`, also held by a CHECK constraint. */
export const VACCINATION_SOURCES = ['gingr', 'intake', 'staff', 'customer'] as const;

export type VaccinationSource = (typeof VACCINATION_SOURCES)[number];

/** A stored value as a known source, or null (unknown / absent / unexpected). */
export function parseVaccinationSource(value: unknown): VaccinationSource | null {
  return typeof value === 'string' &&
    (VACCINATION_SOURCES as readonly string[]).includes(value)
    ? (value as VaccinationSource)
    : null;
}

/** What the desk is told about one record's sign-off. */
export type VaccinationStanding =
  | { kind: 'verified' }
  | { kind: 'from_gingr' }
  | { kind: 'entered_by_staff' }
  | { kind: 'unverified' };

export function vaccinationStanding(row: {
  verified: boolean | null | undefined;
  source?: string | null;
}): VaccinationStanding {
  if (row.verified === true) return { kind: 'verified' };
  switch (parseVaccinationSource(row.source)) {
    case 'gingr':
      return { kind: 'from_gingr' };
    case 'staff':
      return { kind: 'entered_by_staff' };
    case 'intake':
    case 'customer':
    case null:
      return { kind: 'unverified' };
  }
}

/** Short labels for the standing, for a table cell or a list line. */
export const VACCINATION_STANDING_LABEL: Record<VaccinationStanding['kind'], string> = {
  verified: 'Verified',
  from_gingr: 'From Gingr',
  entered_by_staff: 'Entered by staff',
  unverified: 'Unverified',
};

/** Where a row came from, said plainly (the Pet Profile's "Source" column). */
export const VACCINATION_SOURCE_LABEL: Record<VaccinationSource, string> = {
  gingr: 'Gingr',
  intake: 'Uploaded record',
  staff: 'Staff',
  customer: 'Owner',
};

/**
 * One vaccine name, however it was typed: trimmed, lower-cased, inner
 * whitespace collapsed to `_`. The Gingr importer's transform, so "Rabies"
 * typed at the desk and Gingr's `rabies` are the same stored string. The
 * booking gate no longer relies on this alone: since SPEC 2026-10-26 (D2) it
 * matches requirements through `vaccine-catalog.ts`, which also treats
 * spellings of one vaccine (DHPP / DAPP / DHLPP) as the same requirement.
 */
export function normalizeVaccineName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '_');
}
